import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTabs } from '../../../contexts/TabContext';
import MenuService from '../../../services/MenuService';

const SidebarLeft = () => {
  const { t } = useTranslation();
  const { openTab, registerMenuRoutes } = useTabs();
  const location = useLocation();

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [menus, setMenus] = useState([]);
  const [openParents, setOpenParents] = useState({});

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
    if (window.innerWidth >= 768) {
      document.documentElement.classList.toggle('sidebar-left-collapsed');
    }
  };

  const isRouteActive = (route) => {
    if (!route) return false;
    return (
      location.pathname === route ||
      location.pathname.startsWith(`${route}/`) ||
      location.pathname.startsWith(`${route}-`)
    );
  };

  const isParentActive = (menu) => {
    if (!menu?.submenus?.length) {
      return isRouteActive(menu.route);
    }
    return menu.submenus.some((submenu) => isRouteActive(submenu.route));
  };

  const handleLinkClick = (e, path) => {
    e.preventDefault();
    if (!path) return;
    openTab(path);
    if (window.innerWidth < 768) {
      setIsCollapsed(false);
      document.documentElement.classList.remove('sidebar-left-opened');
    }
  };

  const toggleParent = (e, menuId) => {
    e.preventDefault();
    setOpenParents((prev) => ({ ...prev, [menuId]: !prev[menuId] }));
  };

  useEffect(() => {
    const updateSidebarClass = () => {
      if (isCollapsed) {
        document.documentElement.classList.add('sidebar-left-collapsed');
      } else {
        document.documentElement.classList.remove('sidebar-left-collapsed');
      }
    };

    updateSidebarClass();
    return () => {
      document.documentElement.classList.remove('sidebar-left-collapsed');
    };
  }, [isCollapsed]);

  useEffect(() => {
    const nextOpen = {};
    menus.forEach((menu) => {
      if (menu.submenus?.length > 0) {
        nextOpen[menu.id] = isParentActive(menu);
      }
    });
    setOpenParents((prev) => ({ ...nextOpen, ...prev }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, menus]);

  useEffect(() => {
    const fetchMenus = async () => {
      try {
        const response = await MenuService.getCurrentUserMenu();
        if (Array.isArray(response) && response.length > 0) {
          const normalizedMenus = response.map((menu) => ({
            ...menu,
            id: menu.id ?? menu.translationKey ?? menu.name,
            submenus: Array.isArray(menu.submenus) ? menu.submenus.map((submenu) => ({
              ...submenu,
              id: submenu.id ?? submenu.translationKey ?? submenu.name
            })) : []
          }));
          setMenus(normalizedMenus);
          registerMenuRoutes(normalizedMenus);
        } else {
          setMenus([]);
          registerMenuRoutes([]);
        }
      } catch (_) {
        setMenus([]);
        registerMenuRoutes([]);
      }
    };

    fetchMenus();

    const handleCompanyChanged = () => fetchMenus();
    window.addEventListener('companyChanged', handleCompanyChanged);
    return () => window.removeEventListener('companyChanged', handleCompanyChanged);
  }, [registerMenuRoutes]);

  const getLabel = (item) => {
    if (item.translationKey) {
      return t(item.translationKey);
    }
    return item.name || '';
  };

  return (
    <aside id="sidebar-left" className={`sidebar-left ${isCollapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-header">
        <div className="sidebar-title">{t('sidebar.navigation')}</div>
        <div className="sidebar-toggle" onClick={toggleSidebar}>
          <i className="fa fa-bars" aria-label="Toggle sidebar"></i>
        </div>
      </div>

      <div className="nano">
        <div className="nano-content">
          <nav id="menu" className="nav-main" role="navigation">
            <ul className="nav nav-main">
              {menus.map((menu) => {
                const hasChildren = menu.submenus && menu.submenus.length > 0;
                const parentOpen = !!openParents[menu.id];
                const active = isParentActive(menu);
                const tooltip = getLabel(menu);
                const icon = menu.icon || 'fa-circle';

                if (!hasChildren) {
                  return (
                    <li key={menu.id} className={active ? 'nav-active' : ''} data-tooltip={tooltip}>
                      <a href={menu.route || '#'} onClick={(e) => handleLinkClick(e, menu.route)}>
                        <i className={`fa ${icon}`} aria-hidden="true"></i>
                        <span>{tooltip}</span>
                      </a>
                    </li>
                  );
                }

                return (
                  <li key={menu.id} className={`nav-parent ${parentOpen ? 'nav-expanded' : ''}`}>
                    <a href={`#${menu.id}`} onClick={(e) => toggleParent(e, menu.id)} style={{ cursor: 'pointer' }}>
                      <i className={`fa ${icon}`} aria-hidden="true"></i>
                      <span>{tooltip}</span>
                      <i className={`fa ${parentOpen ? 'fa-chevron-down' : 'fa-chevron-right'} nav-parent-arrow`}></i>
                    </a>
                    <ul className="nav nav-children" style={{ display: parentOpen ? 'block' : 'none' }}>
                      {menu.submenus.map((submenu) => {
                        const submenuActive = isRouteActive(submenu.route);
                        const submenuIcon = submenu.icon || 'fa-circle';
                        return (
                          <li key={submenu.id} className={submenuActive ? 'nav-active' : ''}>
                            <a href={submenu.route || '#'} onClick={(e) => handleLinkClick(e, submenu.route)}>
                              <i className={`fa ${submenuIcon}`} aria-hidden="true"></i>
                              <span>{getLabel(submenu)}</span>
                            </a>
                          </li>
                        );
                      })}
                    </ul>
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>
      </div>
    </aside>
  );
};

export default SidebarLeft;
